import React, { Component, Context, ContextType, MouseEvent } from 'react';

import * as Sentry from '@sentry/browser';
import { Helmet } from 'react-helmet';
import { AuthContext, type AuthContextValue } from '~/auth';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
} from '@mui/material';

import ErrorDisplay from '~/components/Common/ErrorDisplay';
import Spinner from '~/components/Common/Spinner';

import { InternalProperty, getInternalProperty } from '~/services/AccommodationService';
import BedbankService from '~/services/BedbankService';
import { HttpResponse } from '~/services/types';

import { isAdmin } from '~/utils/adminPermission';
import { addRecentlyViewedProduct } from '~/utils/recentlyViewedProducts';

import BedbankProgressModal from './BedbankProgressModal';
import BedbankPropertiesEditForm from './Form';
import { OverviewPane } from './OverviewPane';

interface Props {
  match: {
    params: {
      id: string;
    };
  };
}

interface State {
  isAdmin: boolean;
  fetching: boolean;
  scanningRatesData: boolean;
  syncRates: boolean;
  shouldScanRatesPopupBeOpen: boolean;
  scanRatesData?: IAllRatesWithExpediaPromotions;
  updateSellPricesModalVisible: boolean;
  roomMappingModalVisible: boolean;
  roomGenerationModalVisible: boolean;
  availabilityScoreModalVisible: boolean;
  forceDecoupleRoomsModalVisible: boolean;
  forceDecoupleRoomsIds: Array<string>;
  syncContentClickModalVisible: boolean;
  syncReviewsClickModalVisible: boolean;
  result?: App.Bedbank.PropertyEdit;
  accommodationInternalProperty?: InternalProperty;
  error?: any;
}

interface IPropertyResponse {
  status: number;
  message: string;
  result: App.Bedbank.PropertyEdit;
}

interface IAllRatesWithExpediaPromotionsRoomRate {
  rateId: string;
  isNew: boolean;
  isDeleted: boolean;
  isChanged: boolean;
  isRestricted: boolean;
  facilities: Array<string>;
  promotions: Array<string>;
}

interface IAllRatesWithExpediaPromotionsRoom {
  roomId: string;
  roomName: string;
  rates: Array<IAllRatesWithExpediaPromotionsRoomRate>;
}

interface IAllRatesWithExpediaPromotions {
  lastScan: string;
  state: 'scanning' | 'completed' | 'pending';
  rooms: {
    hotelPackage: Array<IAllRatesWithExpediaPromotionsRoom>;
    hotelOnly: Array<IAllRatesWithExpediaPromotionsRoom>;
  };
}

interface IScanResponse {
  status: number;
  message: string;
  result?: IAllRatesWithExpediaPromotions;
}

interface ISyncResponse {
  status: number;
  message: string;
  result?: {
    id: string;
    state: string;
  };
}

const RoomRatesStatuses: {
  [name: string]: {
    label: string;
    color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
  };
} = {
  new: {
    label: 'new',
    color: 'success',
  },
  deleted: {
    label: 'deleted',
    color: 'error',
  },
  changed: {
    label: 'changed',
    color: 'warning',
  },
  restricted: {
    label: 'restricted',
    color: 'info',
  },
};

function getRoomRatesStatus(rate: IAllRatesWithExpediaPromotionsRoomRate) {
  if (rate.isNew) {
    return RoomRatesStatuses.new;
  } else if (rate.isDeleted) {
    return RoomRatesStatuses.deleted;
  } else if (rate.isChanged) {
    return RoomRatesStatuses.changed;
  } else if (rate.isRestricted) {
    return RoomRatesStatuses.restricted;
  }

  return null;
}

class BedbankPropertiesEditPage extends Component<Props, State> {
  static contextType: Context<AuthContextValue> = AuthContext;

  context: ContextType<typeof AuthContext>;

  constructor(props: Props, context) {
    super(props);
    this.state = {
      isAdmin: isAdmin(context.user),
      fetching: true,
      scanningRatesData: false,
      syncRates: true,
      shouldScanRatesPopupBeOpen: false,
      scanRatesData: null,
      roomMappingModalVisible: false,
      updateSellPricesModalVisible: false,
      roomGenerationModalVisible: false,
      availabilityScoreModalVisible: false,
      forceDecoupleRoomsModalVisible: false,
      syncContentClickModalVisible: false,
      syncReviewsClickModalVisible: false,
      forceDecoupleRoomsIds: [],
      result: null,
      accommodationInternalProperty: null,
      error: null,
    };
  }

  componentDidMount = async () => {
    await this.fetchData(this.props.match.params.id);
    await this.syncRatesData();
  };

  fetchData = async (id: string) => {
    this.setState({ fetching: true, error: null }, async () => {
      try {
        const response: IPropertyResponse = await BedbankService.getPropertyEdit(id);

        try {
          const accommodationPropertyResponse: HttpResponse<InternalProperty> = await getInternalProperty({
            internalService: 'bedbank',
            internalServiceId: id,
          });
          this.setState({
            accommodationInternalProperty: accommodationPropertyResponse.result,
          });
        } catch (err) {
          if (err.status === 404) {
            Sentry.captureException(err);
          } else {
            throw err;
          }
        }

        this.setState({
          fetching: false,
          result: response.result,
        });
        addRecentlyViewedProduct({
          id: `bedbank:${response.result.id ?? id}`,
          label: response.result.name,
          href: `${window.location.pathname}${window.location.search}${window.location.hash}`,
          productLabel: 'Bedbank',
        });
      } catch (err) {
        this.setState({ fetching: false, error: err.message });
        Sentry.captureException(err);
      }
    });
  };

  scanRatesPopupClose = () => {
    this.setState({
      shouldScanRatesPopupBeOpen: false,
    });
  };

  setProperty = (property: App.Bedbank.PropertyEdit) => {
    this.setState({
      result: property,
    });
  };

  syncRatesData = async () => {
    try {
      const response: ISyncResponse = await BedbankService.getRatesSync(this.props.match.params.id);

      const syncRatesData = response.result?.state;

      if (!syncRatesData) {
        this.setState({
          syncRates: false,
        });
      }

      if (syncRatesData) {
        setTimeout(() => this.syncRatesData(), 3000);
      }
    } catch (err) {
      this.setState({
        syncRates: false,
      });
    }
  };

  scanRatesData = async () => {
    try {
      const response: IScanResponse = await BedbankService.getRatesScan(this.props.match.params.id);

      const scanningRatesData = !(response.result?.state === 'completed' || !response.result?.state);

      if (!scanningRatesData) {
        this.setState({
          scanningRatesData: false,
          scanRatesData: response.result,
        });
      }

      if (scanningRatesData) {
        setTimeout(() => this.scanRatesData(), 3000);
      }
    } catch (err) {
      this.setState({
        scanRatesData: null,
        scanningRatesData: false,
      });
    }
  };

  fetchScanRatesData = async (event: MouseEvent) => {
    event.preventDefault();
    this.setState(
      {
        shouldScanRatesPopupBeOpen: true,
        scanningRatesData: true,
      },
      this.scanRatesData,
    );
  };

  postScanRatesData = async (event: MouseEvent) => {
    event.preventDefault();
    this.setState(
      {
        scanningRatesData: true,
      },
      async () => {
        try {
          await BedbankService.postRatesScan(this.props.match.params.id);
          this.scanRatesData();
        } catch (err) {
          this.setState({ scanningRatesData: false });
        }
      },
    );
  };

  postSyncRatesData = async (event: MouseEvent) => {
    event.preventDefault();
    this.setState(
      {
        syncRates: true,
      },
      async () => {
        try {
          await BedbankService.postRatesSync(this.props.match.params.id);
          setTimeout(() => this.syncRatesData(), 5000);
        } catch (err) {
          this.setState({ syncRates: false });
        }
      },
    );
  };

  showUpdateSellPricesModal = () => {
    this.setState({
      updateSellPricesModalVisible: true,
    });
  };

  showRoomMappingModal = () => {
    this.setState({
      roomMappingModalVisible: true,
    });
  };

  showRoomGenerationModal = () => {
    this.setState({
      roomGenerationModalVisible: true,
    });
  };

  showAvailabilityScoreModal = () => {
    this.setState({
      availabilityScoreModalVisible: true,
    });
  };

  showForceDecoupleRoomsModal = (ids: Array<string>) => {
    this.setState({
      forceDecoupleRoomsIds: ids,
      forceDecoupleRoomsModalVisible: true,
    });
  };

  syncContentModal = () => {
    this.setState({
      syncContentClickModalVisible: true,
    });
  };

  syncReviewsModal = () => {
    this.setState({
      syncReviewsClickModalVisible: true,
    });
  };

  getUniqueRoomIds = (hotelOnly, hotelPackage): Array<string> => {
    return Array.from(new Set([...hotelOnly, ...hotelPackage].map((room) => room.roomId)));
  };

  rateItem = (id: string, title: string, rates: Array<IAllRatesWithExpediaPromotionsRoomRate>) => (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls={id} id={id}>
        {title}
      </AccordionSummary>
      <AccordionDetails>
        <Box>
          {rates.map((rate) => (
            <Box
              key={rate.rateId}
              sx={{
                p: 1,
                mb: 1,
                border: 1,
                borderRadius: '12px',
              }}
            >
              {getRoomRatesStatus(rate) && (
                <Chip
                  label={getRoomRatesStatus(rate).label}
                  color={getRoomRatesStatus(rate).color}
                  sx={{
                    position: 'absolute',
                    right: '20px',
                    mt: '-5px',
                  }}
                />
              )}
              <div>ID: {rate.rateId}</div>
              <div>Promo: {rate.promotions[0] || 'None'}</div>
              <div>
                All inclusions:
                <ul>
                  {rate.facilities.map((facility) => (
                    <li key={facility}>{facility}</li>
                  ))}
                </ul>
              </div>
              {this.getSpecialDescriptions(rate.rateId) && (
                <div>
                  <p>Special Extra Description:</p>
                  <div dangerouslySetInnerHTML={{ __html: this.getSpecialDescriptions(rate.rateId) }}></div>
                </div>
              )}
            </Box>
          ))}
        </Box>
      </AccordionDetails>
    </Accordion>
  );

  getSpecialDescriptions = (rateId: string) => {
    const { rates } = this.state.result;
    const rate = rates.find((rate) => rate.extId === rateId);
    return rate?.specialOfferDescription ?? null;
  };

  roomRateItems = (data: IAllRatesWithExpediaPromotionsRoom) => {
    const withPromos = data?.rates.filter((rate) => rate.promotions.length > 0);
    const withoutPromos = data?.rates.filter((rate) => !rate.promotions.length);
    return (
      <Box>
        {data ? (
          <>
            <Box>Room ID: {data.roomId}</Box>
            <div>
              <b>{data.roomName}</b>
            </div>
            <Box>
              Rates:
              {this.rateItem(`${data.roomId}-with-promo`, `with promos (${withPromos.length})`, withPromos)}
              {this.rateItem(`${data.roomId}-without-promo`, `without promos (${withoutPromos.length})`, withoutPromos)}
            </Box>
          </>
        ) : (
          'Not available'
        )}
      </Box>
    );
  };

  render() {
    const { fetching, error, result } = this.state;

    if (fetching) {
      return <Spinner />;
    }

    if (error) {
      return <ErrorDisplay message={error} />;
    }

    const hotelPackage = this.state.scanRatesData?.rooms?.hotelPackage ?? [];
    const hotelOnly = this.state.scanRatesData?.rooms?.hotelOnly ?? [];
    const roomIds = this.getUniqueRoomIds(hotelOnly, hotelPackage);

    return (
      <div className="offer-deal">
        <Helmet>
          <title>Property | {result?.name || ''}</title>
        </Helmet>

        <Container>
          <OverviewPane
            property={result}
            syncRates={this.state.syncRates}
            fetchScanRatesData={this.fetchScanRatesData}
            postSyncRatesData={this.postSyncRatesData}
            onUpdateSellPricesClick={this.showUpdateSellPricesModal}
            onRoomMappingClick={this.showRoomMappingModal}
            onAvailabilityScoreClick={this.showAvailabilityScoreModal}
            onRoomGenerationClick={this.showRoomGenerationModal}
            onSyncContentClick={this.syncContentModal}
            onSyncReviewsClick={this.syncReviewsModal}
          />

          <BedbankPropertiesEditForm
            property={result}
            setProperty={this.setProperty}
            isAdmin={this.state.isAdmin}
            onForceDecoupleRoomsClick={this.showForceDecoupleRoomsModal}
            accommodationInternalProperty={this.state.accommodationInternalProperty || undefined}
          />
        </Container>

        <Dialog open={this.state.shouldScanRatesPopupBeOpen} onClose={this.scanRatesPopupClose} maxWidth="md" fullWidth>
          <DialogTitle>Rates</DialogTitle>
          <DialogContent>
            {this.state.scanningRatesData && <Spinner size={48} />}

            {!this.state.scanningRatesData && (
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-evenly',
                    textAlign: 'center',
                  }}
                >
                  <h2>Hotel Only</h2>
                  <h2>Hotel Package</h2>
                </Box>

                {roomIds.map((roomId) => (
                  <>
                    <Divider sx={{ mb: 2 }} />
                    <Box
                      key={roomId}
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        mb: 2,
                        justifyContent: 'space-evenly',
                      }}
                    >
                      {this.roomRateItems(hotelOnly.find((room) => room.roomId === roomId))}
                      {this.roomRateItems(hotelPackage.find((room) => room.roomId === roomId))}
                    </Box>
                  </>
                ))}
              </Box>
            )}

            {!this.state.scanningRatesData && hotelPackage.length + hotelOnly.length === 0 && (
              <DialogContentText variant="h5">No results</DialogContentText>
            )}
          </DialogContent>

          <DialogActions>
            <Button variant="text" onClick={this.postScanRatesData} disabled={this.state.scanningRatesData}>
              Scan rates
            </Button>
          </DialogActions>
        </Dialog>

        <BedbankProgressModal
          open={this.state.roomMappingModalVisible}
          modalTitle="Map Rooms"
          confirmationText="will kick off a room mapping process"
          startJob={() => BedbankService.runRoomMapping(this.props.match.params.id)}
          onClose={() => this.setState({ roomMappingModalVisible: false })}
        />

        <BedbankProgressModal
          open={this.state.updateSellPricesModalVisible}
          modalTitle="Update Sell Prices"
          confirmationText="will kick off updating sell prices process"
          startJob={() => BedbankService.updateSellPrices(this.props.match.params.id)}
          onClose={() => this.setState({ updateSellPricesModalVisible: false })}
        />

        <BedbankProgressModal
          open={this.state.roomGenerationModalVisible}
          modalTitle="Generate Rooms"
          confirmationText="will kick off a room generation process"
          startJob={() => BedbankService.runRoomGeneration(this.props.match.params.id)}
          onClose={() => this.setState({ roomGenerationModalVisible: false })}
        />

        <BedbankProgressModal
          open={this.state.availabilityScoreModalVisible}
          modalTitle="Availability Score"
          confirmationText="will kick off an availability score process"
          startJob={() => BedbankService.runAvailabilityScore(this.props.match.params.id)}
          onClose={() => this.setState({ availabilityScoreModalVisible: false })}
        />

        <BedbankProgressModal
          open={this.state.forceDecoupleRoomsModalVisible}
          modalTitle="Force Decouple"
          confirmationText="will kick off selected rooms decoupling process"
          startJob={() => BedbankService.runDecoupleRooms(this.props.match.params.id, this.state.forceDecoupleRoomsIds)}
          onClose={() => this.setState({ forceDecoupleRoomsModalVisible: false, forceDecoupleRoomsIds: [] })}
        />

        <BedbankProgressModal
          open={this.state.syncContentClickModalVisible}
          modalTitle="Sync Suppliers Content"
          confirmationText="will kick off a sync content process"
          startJob={() => BedbankService.syncPropertyContent(this.props.match.params.id)}
          onClose={() => this.setState({ syncContentClickModalVisible: false })}
        />

        <BedbankProgressModal
          open={this.state.syncReviewsClickModalVisible}
          modalTitle="Sync Suppliers Reviews"
          confirmationText="will kick off a sync reviews process"
          startJob={() => BedbankService.syncPropertyReviews(this.props.match.params.id)}
          onClose={() => this.setState({ syncReviewsClickModalVisible: false })}
        />
      </div>
    );
  }
}

export default BedbankPropertiesEditPage;
